<template>
    <div class="chart-container">
        <v-chart v-if="needRefresh" class="chart" :option="option" autoresize />
    </div>
</template>

<script setup>
    import { inject, onMounted, onUpdated, provide, ref } from 'vue';
    import { use } from "echarts/core";
    import { CanvasRenderer } from "echarts/renderers";
    import { ScatterChart } from "echarts/charts";
    import { TooltipComponent, DataZoomComponent, LegendComponent } from "echarts/components";
    import VChart, { THEME_KEY } from "vue-echarts";

    use([
        CanvasRenderer,
        ScatterChart,
        TooltipComponent,
        DataZoomComponent,
        LegendComponent,
    ]);

    const translate = inject('translate');
    const startLoading = inject('startLoading');
    const stopLoading = inject('stopLoading');

    const needRefresh = ref(false);
    const option = ref({
        dataZoom: [
            {
                id: 'dataZoomX',
                filterMode: 'filter',
                labelFormatter: function (value, valueStr) {
                    if (typeof valueStr != 'string' && valueStr.length > 5)
                        return valueStr.split(' ')[1].substr(0, 5);
                },
                type: 'slider',
                xAxisIndex: [0],
            },
            {
                id: 'dataZoomY',
                filterMode: 'empty',
                type: 'slider',
                right: '30',
                yAxisIndex: [0],
            }
        ],
        tooltip: {
            showDelay: 0,
            formatter: function (params) {
                var tooltip = '<b>' + params.value[1] + '</b> :<br/>' + params.value[2] + ' (' + params.value[3] + ')<br/>';
                var batches = params.value[4];

                if (batches != null)
                    for (var i = 0; i < batches.length; i++) {
                        var batch = batches[i];
                        var breakLine = (i == batches.length - 1) ? '' : '<br/>';

                        var batchLine = (!batch.enabled) ? '🛑 ' : '✅ ';
                        batchLine += batch.name;
                        batchLine += (batch.action != null) ? ' (' + batch.action + ')' : '';

                        tooltip += batchLine + breakLine;
                    }

                return tooltip;
            },
            axisPointer: {
                show: true,
                type: 'cross',
                lineStyle: {
                    type: 'dashed',
                    width: 1
                },
            },
        },
        legend: {
            data: [
                {
                    name: translate('disabled', 'echarts'),
                    itemStyle: {
                        color: '#cf6679',
                    },
                },
                {
                    name: translate('manyDisabled', 'echarts'),
                    itemStyle: {
                        color: '#bb86fc',
                    },
                },
                {
                    name: translate('enabled', 'echarts'),
                    itemStyle: {
                        color: '#03dac5',
                    },
                },
            ],
            left: 10,
            top: 10,
        },
        xAxis: [
            {
                type: 'time',
                scale: true,
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed',
                    },
                },
            },
        ],
        yAxis: [
            {
                type: 'category',
                scale: true,
                splitLine: {
                    show: false,
                },
            },
        ],
        series: [],
    });

    provide(THEME_KEY, 'dark');

    onMounted(() => {
        startLoading();
    });

    onUpdated(() => {
        stopLoading();
    });

    // SchedulerTab.vue reaches into option/needRefresh through a template ref — only visible
    // through defineExpose() on a <script setup> component.
    defineExpose({ option, needRefresh });
</script>

<style scoped>
    @media only screen and (max-height: 600px) {
    }

    .chart-container {
        height: 400px;
    }

    .chart {
        height: 100%;
    }
</style>
